import React, { useState } from 'react';
import { Form, Select, DatePicker, Upload, Button, Space, message } from 'antd';
import { Staff } from '../../services/staffs';
import { UploadOutlined, EyeOutlined, DeleteOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd';
import dayjs from 'dayjs';
import api from '../../services/api';
import customerApi from '../../services/customerApi';

interface User {
  id: number;
  name: string;
  email: string;
}

interface EngagementTabProps {
  form: any;
  staff: User[];
  teams?: any[];
  isEditing?: boolean;
  isCustomerPortal?: boolean;
  engagementFiles?: {
    account_attachment?: UploadFile[];
    tax_attachment?: UploadFile[];
    audit_attachment?: UploadFile[];
    secretary_attachment?: UploadFile[];
  };
  onFileChange?: (field: string, files: UploadFile[]) => void;
  businessId?: number;
  onEngagementDateChange?: () => void;
  latestRequest?: any;
}

interface EngagementRow {
  key: string;
  label: string;
  staffField: string;
  appointDateField: string;
  resignDateField: string;
  attachmentField?: string;
  hasAttachment: boolean;
}

export const BusinessEngagementTab: React.FC<EngagementTabProps> = ({
  form,
  staff,
  teams = [],
  isEditing = false,
  isCustomerPortal = false,
  engagementFiles = {},
  onFileChange,
  businessId,
  onEngagementDateChange,
  latestRequest,
}) => {
  // In customer portal, always show as read-only
  const canEdit = isEditing && !isCustomerPortal;
  const [fileActionLoading, setFileActionLoading] = useState<Record<string, { view?: boolean }>>({});
  const getRequestedValue = (fieldName: string): any => {
    if (!latestRequest) return null;
    return (latestRequest as any)[fieldName] ?? null;
  };

  const normalizeRequestedDate = (value: any): string => {
    if (!value) return '';
    if (dayjs.isDayjs(value)) return value.format('YYYY-MM-DD');
    if (typeof value === 'string') return dayjs(value).isValid() ? dayjs(value).format('YYYY-MM-DD') : '';
    return '';
  };

  const renderRequestedText = (value: string | null) => {
    if (!value) return null;
    return (
      <div style={{ marginTop: 6, fontSize: 12, color: '#faad14' }}>
        Requested: {value}
      </div>
    );
  };

  const setFileLoading = (field: string, action: 'view', isLoading: boolean) => {
    setFileActionLoading((prev) => ({
      ...prev,
      [field]: {
        ...prev[field],
        [action]: isLoading,
      },
    }));
  };

  const handleView = async (field: string, file: UploadFile) => {
    if (!file || !businessId) return;
    
    try {
      setFileLoading(field, 'view', true);
      // If it's a new file (has originFileObj), use it directly
      if (file.originFileObj) {
        const blobUrl = URL.createObjectURL(file.originFileObj);
        const newWindow = window.open(blobUrl, '_blank');

        if (newWindow) {
          newWindow.addEventListener('beforeunload', () => {
            URL.revokeObjectURL(blobUrl);
          });
        }
        setTimeout(() => {
          try {
            URL.revokeObjectURL(blobUrl);
          } catch (e) {
            // Ignore errors if URL was already revoked
          }
        }, 60000); // 60 seconds
        return;
      }

      // For existing files from S3, fetch via API
      const isCustomerPortalPath = window.location.pathname.startsWith('/customer');
      let url: string;
      let apiClient: any;
      
      if (isCustomerPortalPath) {
        url = `/customer/businesses/${businessId}/engagement-files/${field}/view`;
        apiClient = customerApi;
      } else {
        url = `/businesses/${businessId}/engagement-files/${field}/view`;
        apiClient = api;
      }
      
      const response = await apiClient.get(url, {
        responseType: 'blob',
      });
      
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error: any) {
      message.error('Failed to view file');
    } finally {
      setFileLoading(field, 'view', false);
    }
  };

  const handleViewRequested = async (field: string) => {
    if (!businessId || isCustomerPortal) return;
    try {
      setFileLoading(`request_${field}`, 'view', true);
      const response = await api.get(`/business-requests/${businessId}/engagement/${field}/view`, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: response.headers['content-type'] || 'application/octet-stream',
      });
      const blobUrl = window.URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60000);
    } catch (error: any) {
      message.error('Failed to view requested file');
    } finally {
      setFileLoading(`request_${field}`, 'view', false);
    }
  };

  const renderAttachmentField = (field: string, files?: UploadFile[]) => {
    const hasRequestedFile = !!getRequestedValue(field);
    if (canEdit) {
      const hasFile = !!files?.length;
      return (
        <Space size="small">
          <Upload
            fileList={files || []}
            showUploadList={false}
            beforeUpload={() => false}
            onChange={(info) => {
              if (onFileChange) {
                onFileChange(field, info.fileList);
              }
            }}
            maxCount={1}
            accept=".update,.png,.jpg,.jpeg"
          >
            <Button size="small" icon={<UploadOutlined />} style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }} />
          </Upload>
          {hasFile && (
            <>
              <Button
                size="small"
                icon={<EyeOutlined />}
                loading={fileActionLoading[field]?.view}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  const file = files?.[0];
                  if (file && businessId) {
                    handleView(field, file);
                  }
                }}
              />
              <Button
                size="small"
                danger
                icon={<DeleteOutlined />}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  if (onFileChange) {
                    onFileChange(field, []);
                  }
                }}
              />
            </>
          )}
          {hasRequestedFile && (
            <Button
              size="small"
              icon={<EyeOutlined style={{ color: '#faad14' }} />}
              loading={fileActionLoading[`request_${field}`]?.view}
              style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
              onClick={() => handleViewRequested(field)}
            />
          )}
        </Space>
      );
    } else {
      return (
        <div>
          {files && files.length > 0 ? (
            <Space size="small">
              <Button
                size="small"
                icon={<EyeOutlined />}
                style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                onClick={() => {
                  const file = files[0];
                  if (file && businessId) {
                    handleView(field, file);
                  }
                }}
              />
              {hasRequestedFile && (
                <Button
                  size="small"
                  icon={<EyeOutlined style={{ color: '#faad14' }} />}
                  style={{ width: 28, height: 28, padding: 0, borderRadius: 999 }}
                  onClick={() => handleViewRequested(field)}
                />
              )}
            </Space>
          ) : (
            <div style={{ color: '#999', fontSize: '12px' }}>No attachment</div>
          )}
        </div>
      );
    }
  };

  const engagementRows: EngagementRow[] = [
    {
      key: 'advisor',
      label: 'Advisor',
      staffField: 'advisor_staff_id',
      appointDateField: 'advisor_appoint_date',
      resignDateField: 'advisor_resign_date',
      hasAttachment: false,
    },
    {
      key: 'account',
      label: 'Accountant',
      staffField: 'account_staff_id',
      appointDateField: 'account_appoint_date',
      resignDateField: 'account_resign_date',
      attachmentField: 'account_attachment',
      hasAttachment: true,
    },
    {
      key: 'tax',
      label: 'Tax Agent',
      staffField: 'tax_staff_id',
      appointDateField: 'tax_appoint_date',
      resignDateField: 'tax_resign_date',
      attachmentField: 'tax_attachment',
      hasAttachment: true,
    },
    {
      key: 'audit',
      label: 'Auditor',
      staffField: 'audit_staff_id',
      appointDateField: 'audit_appoint_date',
      resignDateField: 'audit_resign_date',
      attachmentField: 'audit_attachment',
      hasAttachment: true,
    },
    {
      key: 'secretary',
      label: 'Secretary',
      staffField: 'secretary_staff_id',
      appointDateField: 'secretary_appoint_date',
      resignDateField: 'secretary_resign_date',
      attachmentField: 'secretary_attachment',
      hasAttachment: true,
    },
  ];

  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ borderBottom: '1px solid #f0f0f0' }}>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', width: '150px', borderRight: '1px solid #f0f0f0' }}></th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Staff</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Appoint Date</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa', borderRight: '1px solid #f0f0f0' }}>Resign Date</th>
            <th style={{ padding: '12px', textAlign: 'left', fontWeight: 600, backgroundColor: '#fafafa' }}>Attachment</th>
          </tr>
        </thead>
        <tbody>
          {engagementRows.map((row) => (
            <tr key={row.key} style={{ borderBottom: '1px solid #f0f0f0' }}>
              <td style={{ padding: '12px', fontWeight: 600, backgroundColor: '#fafafa', width: '150px', borderRight: '1px solid #f0f0f0' }}>
                {row.label}
              </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              {canEdit ? (
                  <Form.Item name={row.staffField} style={{ margin: 0 }}>
                    <Select allowClear placeholder="Select staff" style={{ width: '100%' }}>
                      {staff.map((staffItem) => (
                        <Select.Option key={staffItem.id} value={staffItem.id}>
                          {staffItem.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
              ) : null}
                {(() => {
                  const staffId = form.getFieldValue(row.staffField);
                  const foundStaff = staff.find((s) => s.id === staffId);
                  const requestedStaffId = getRequestedValue(row.staffField);
                  const requestedStaff = staff.find((s) => String(s.id) === String(requestedStaffId));
                  const hasRequested = requestedStaffId && String(requestedStaffId) !== String(staffId || '');
                  return (
                    <div>
                      {!canEdit && (foundStaff?.name || '-')}
                      {hasRequested && renderRequestedText(requestedStaff?.name || String(requestedStaffId))}
                    </div>
                  );
                })()}
            </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              {canEdit ? (
                  <Form.Item 
                    name={row.appointDateField} 
                    style={{ margin: 0 }}
                    dependencies={[row.resignDateField]}
                  >
                    <DatePicker 
                      style={{ width: '100%' }} 
                      format="DD/MM/YYYY"
                      onChange={(date) => {
                        // Ensure date is set to start of day to avoid timezone issues
                        if (date) {
                          form.setFieldValue(row.appointDateField, date.startOf('day'));
                        } else {
                          form.setFieldValue(row.appointDateField, null);
                        }
                        if (onEngagementDateChange) {
                          onEngagementDateChange();
                        }
                      }}
                    />
          </Form.Item>
              ) : null}
                {(() => {
                  const appointDate = form.getFieldValue(row.appointDateField);
                  const requestedDate = getRequestedValue(row.appointDateField);
                  const hasRequested = requestedDate && normalizeRequestedDate(requestedDate) !== normalizeRequestedDate(appointDate);
                  return (
                    <div>
                      {!canEdit && (appointDate ? dayjs(appointDate).format('DD/MM/YYYY') : '-')}
                      {hasRequested && renderRequestedText(dayjs(requestedDate).format('DD/MM/YYYY'))}
                    </div>
                  );
                })()}
            </td>
              <td style={{ padding: '12px', borderRight: '1px solid #f0f0f0' }}>
              {canEdit ? (
                  <Form.Item 
                    name={row.resignDateField} 
                    style={{ margin: 0 }}
                    dependencies={[row.appointDateField]}
                  >
                  <DatePicker 
                    style={{ width: '100%' }} 
                    format="DD/MM/YYYY"
                    onChange={(date) => {
                      // Ensure date is set to start of day to avoid timezone issues
                      if (date) {
                        form.setFieldValue(row.resignDateField, date.startOf('day'));
                      } else {
                        form.setFieldValue(row.resignDateField, null);
                      }
                      if (onEngagementDateChange) {
                        onEngagementDateChange();
                      }
                    }}
                  />
                </Form.Item>
              ) : null}
                {(() => {
                  const resignDate = form.getFieldValue(row.resignDateField);
                  const requestedDate = getRequestedValue(row.resignDateField);
                  const hasRequested = requestedDate && normalizeRequestedDate(requestedDate) !== normalizeRequestedDate(resignDate);
                  return (
                    <div>
                      {!canEdit && (resignDate ? dayjs(resignDate).format('DD/MM/YYYY') : '-')}
                      {hasRequested && renderRequestedText(dayjs(requestedDate).format('DD/MM/YYYY'))}
                    </div>
                  );
                })()}
            </td>
            <td style={{ padding: '12px' }}>
                {row.hasAttachment ? (
                  renderAttachmentField(row.attachmentField!, engagementFiles[row.attachmentField as keyof typeof engagementFiles])
              ) : (
                  <div style={{ color: '#999', fontSize: '12px' }}>-</div>
              )}
            </td>
          </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
